/**
 * The browser side of the OVL-016 host: a real canvas per surface and a real worker to own it.
 *
 * Split from `surfaceLayer` so the layer's rules are tested with no DOM and no worker anywhere.
 */

import type { Box } from "../scene/sceneTree";
import type {
  SurfaceCanvas,
  SurfaceMessage,
  SurfacePorts,
  SurfaceReport,
  SurfaceWorker,
} from "./surfaceLayer";


/** How a surface resolves to the module a worker imports. Its budget rides on the node. */
export interface SurfaceCatalogue {
  moduleUrl(activityId: string, surface: string): string;
}

function makeCanvas(parent: HTMLElement): SurfaceCanvas {
  const canvas = document.createElement("canvas");
  canvas.style.position = "absolute";
  canvas.style.pointerEvents = "none";
  parent.appendChild(canvas);

  let width = 0;
  let height = 0;

  return {
    place(box: Box) {
      const rect = parent.getBoundingClientRect();
      width = Math.max(1, Math.round(box.width * rect.width));
      height = Math.max(1, Math.round(box.height * rect.height));
      canvas.style.left = `${String(box.left * 100)}%`;
      canvas.style.top = `${String(box.top * 100)}%`;
      canvas.style.width = `${String(box.width * 100)}%`;
      canvas.style.height = `${String(box.height * 100)}%`;
    },
    detach() {
      canvas.width = width;
      canvas.height = height;
      return canvas.transferControlToOffscreen();
    },
    get width() {
      return width;
    },
    get height() {
      return height;
    },
    remove() {
      canvas.remove();
    },
  };
}

function openWorker(): SurfaceWorker {
  const worker = new Worker(new URL("./workerEntry.ts", import.meta.url), { type: "module" });

  return {
    post(message: SurfaceMessage, transfer?: unknown[]) {
      worker.postMessage(message, (transfer ?? []) as Transferable[]);
    },
    onReport(listen: (report: SurfaceReport) => void) {
      worker.onmessage = (event: MessageEvent<SurfaceReport>) => {
        listen(event.data);
      };
    },
    terminate() {
      worker.terminate();
    },
  };
}

/** Ports backed by the real browser, for a canvas rendering into `parent`. */
export function browserSurfacePorts(
  parent: HTMLElement,
  catalogue: SurfaceCatalogue,
): SurfacePorts {
  return {
    moduleUrl: (activityId, surface) => catalogue.moduleUrl(activityId, surface),
    openWorker,
    makeCanvas: () => makeCanvas(parent),
    report: (line) => {
      console.warn(`[overlay] ${line}`);
    },
  };
}
